import { Layout, Link, Spacer } from '@loomhq/lens';
import cn from 'classnames';
import $ from 'jquery';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { updateAddReplyId } from 'actions/comments';
import { requestVideoPlayerCurrentTime } from 'actions/videoPlayer';
import { COMMENTS_BREAKPOINT } from 'constants/breakpoints';
import { VIDEO_TIMESTAMP_CLASS } from 'constants/comments';
import { REPLY_TO_COMMENT_PARAM } from 'constants/urlParams';
import { deleteCommentCreator } from 'creators/comments';
import { videoPlayerHideComments } from 'creators/videoPlayer';
import { getCloudfrontURI } from 'utilities/avatar';
import {
  getFormattedDateForHeader,
  playVideoAtTimestamp,
  userCanDelete,
} from 'utilities/comments';
import { secondsToVideoTS } from 'utilities/time';
import { getPageLoadParam } from 'utilities/urlParams';

import useMedia from 'hooks/useMedia';
import { selectCurrentUserIsOwner } from 'selectors/videoPlayer';

import AddReply from '../add-reply';
import Reply from '../comment-reply';
import DeleteUndoPlaceholder from '../delete-undo-placeholder';
import PostBody from '../post-body';

import UserAvatar from 'components/user-avatar';

import styles from './styles.module.less';

const BOTTOM_SCROLL_BUFFER = 70;
const SCROLL_ANIMATION_MS = 700;
const AVATAR_SIZE_FULL = 4;
const AVATAR_SIZE_SMALL = 3;

const Comment = props => {
  const dispatch = useDispatch();
  const { comment, scrollContainer, drawnOnSharePage } = props;

  const user = useSelector(state => state.user);
  const videoPlayer = useSelector(state => state.video_player);
  const addReplyId = useSelector(state => state.comments.add_reply_id);
  const replyingToThisComment = addReplyId === comment.id;

  const { video } = videoPlayer;
  const { comments_enabled: commentsEnabled } = video;
  const currentUserIsOwner = useSelector(selectCurrentUserIsOwner);

  // accounts for the case where scrollToAddReply needs to be set = true via
  // reply from an email url
  const replyToCommentViaUrl = getPageLoadParam(REPLY_TO_COMMENT_PARAM);
  const [scrollToAddReply, setScrollToAddReply] = useState(
    replyToCommentViaUrl
  );

  const [disableDelete, setDisableDelete] = useState(false);

  const commentRef = useRef(null);
  const addReplyRef = useRef(null);

  const replyElements =
    comment.replies &&
    comment.replies.map(reply => {
      return <Reply key={reply.id} reply={reply} />;
    });

  let avatar = comment.avatar && comment.avatar.thumb;

  if (avatar) {
    avatar = getCloudfrontURI(avatar);
  }

  const scrollToReply = useCallback(
    targetElement => {
      if (!scrollContainer || !targetElement) {
        return;
      }

      if (!drawnOnSharePage) {
        // embed version of scroll
        const offsetBottom =
          targetElement.offsetTop + targetElement.offsetHeight;
        const targetOffset =
          offsetBottom - scrollContainer.offsetHeight + BOTTOM_SCROLL_BUFFER;

        $(scrollContainer).animate(
          {
            scrollTop: targetOffset,
          },
          SCROLL_ANIMATION_MS
        );
      } else {
        // share page version of scroll
        const targetOffset =
          window.scrollY +
          targetElement.getBoundingClientRect().bottom -
          window.innerHeight +
          BOTTOM_SCROLL_BUFFER;

        scrollContainer.scrollTop = targetOffset;
      }
    },
    [scrollContainer, drawnOnSharePage]
  );

  // handle scrolling to AddReply box
  useEffect(() => {
    if (replyingToThisComment && scrollToAddReply) {
      const addReplyDOMNode = addReplyRef.current;

      if (!addReplyDOMNode) {
        return;
      }

      scrollToReply(addReplyDOMNode);
      setScrollToAddReply(false);
      addReplyDOMNode.focus();
    }
  }, [addReplyId, replyingToThisComment, scrollToAddReply, scrollToReply]);

  useEffect(() => {
    if (comment.locallyDeleted === false) {
      setDisableDelete(false);
    }
  }, [comment.locallyDeleted]);

  const deleteComment = () => {
    if (!disableDelete) {
      setDisableDelete(true);
      dispatch(deleteCommentCreator(props.comment.id));
    }
  };

  const showAddReply = () => {
    dispatch(updateAddReplyId(props.comment.id));
    setScrollToAddReply(true);
  };

  const handleClickTimestamp = event => {
    playVideoAtTimestamp.call(event.target, event);
    dispatch(videoPlayerHideComments());
    dispatch(requestVideoPlayerCurrentTime(comment.time_stamp));

    if (drawnOnSharePage) {
      window.scroll(0, 0);
    }
  };

  const fullSize = useMedia(
    [`(min-width: ${COMMENTS_BREAKPOINT}px)`],
    [true],
    false
  );
  const avatarSize = fullSize ? AVATAR_SIZE_FULL : AVATAR_SIZE_SMALL;

  function VisibleComment() {
    return (
      <>
        <UserAvatar
          className={styles.avatar}
          avatarSrc={avatar}
          name={comment.user_name}
          avatarSize={avatarSize}
        />
        <div
          ref={commentRef}
          className={styles.commentPost}
          data-seconds={comment.time_stamp}
        >
          <div className={styles.commentHeader}>
            <span className={styles.userName}> {comment.user_name} </span>
            <span className={styles.dimmedText}> at </span>
            <span className={styles.dimmedText}>
              <Link
                variant="neutral"
                href="#"
                className={VIDEO_TIMESTAMP_CLASS}
                onClick={handleClickTimestamp}
                data-seconds={comment.time_stamp}
              >
                {secondsToVideoTS(comment.time_stamp)}
              </Link>
            </span>
          </div>
          <PostBody content={comment}></PostBody>
          {commentsEnabled && (
            <div className={styles.dimmedText}>
              <span>{getFormattedDateForHeader(comment.createdAt)}</span>
              {userCanDelete(comment, currentUserIsOwner, user) && (
                <Link
                  variant="neutral"
                  className={styles.actionLink}
                  onClick={deleteComment}
                >
                  Delete
                </Link>
              )}
              <Link
                variant="neutral"
                className={cn(styles.actionLink, 'replyButton')}
                onClick={showAddReply}
              >
                Reply
              </Link>
            </div>
          )}

          {commentsEnabled &&
            replyElements &&
            Boolean(replyElements.length) && <>{replyElements}</>}
          {commentsEnabled && replyingToThisComment && (
            <>
              <Spacer top="large" />
              <AddReply ref={addReplyRef} addReplyId={addReplyId} />
            </>
          )}
        </div>
      </>
    );
  }

  return (
    <Layout gap="medium" className={styles.comment}>
      {comment.locallyDeleted ? (
        <DeleteUndoPlaceholder commentOrReply={comment}></DeleteUndoPlaceholder>
      ) : (
        VisibleComment()
      )}
    </Layout>
  );
};

export default Comment;
